import { createFileRoute, redirect } from "@tanstack/react-router";
import { fetchAuthSession } from "aws-amplify/auth";
import { LoginForm } from "../components/auth/LoginForm";

interface LoginSearch {
  email?: string;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    return {
      email: typeof search.email === "string" ? search.email : undefined,
    };
  },
  beforeLoad: async () => {
    // Check if user is already authenticated via AWS Amplify session
    try {
      const session = await fetchAuthSession();
      // If we have valid tokens, user is authenticated - redirect to home
      if (session.tokens?.idToken && session.tokens?.accessToken) {
        throw redirect({ to: "/" });
      }
    } catch (error: unknown) {
      // Re-throw redirects (TanStack Router redirect objects have isRedirect property)
      if (error && typeof error === "object" && "isRedirect" in error) {
        throw error;
      }
      // User is not authenticated, allow access to login page
      console.log("User not authenticated, showing login page");
    }
  },
  component: LoginComponent,
});

function LoginComponent() {
  const { email } = Route.useSearch();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex">
      {/* Left side - Branding/Hero Section */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          background:
            // "#000100",
            "linear-gradient(to bottom right, #f9cf7b 20%, #000100 90%)",
        }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-40 right-20 w-48 h-48 bg-white/5 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-white/15 rounded-full blur-lg"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-start p-16 text-white">
          <div className="mb-8">
            <img
              src="/icons/logo-white.svg"
              alt="Aitisal Logo"
              className="h-12 w-auto mb-6"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <h1 className="text-5xl font-bold mb-4 leading-tight">
              Welcome to
              <br />
              <span className="text-yellow-200">Aitisal</span>
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-md">
              The complete restaurant management solution that helps you
              streamline operations, boost efficiency, and grow your business.
            </p>
          </div>

          <div className="space-y-4 text-white/80">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-yellow-300 rounded-full"></div>
              <span>Real-time order management</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-yellow-300 rounded-full"></div>
              <span>Advanced analytics & reporting</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-yellow-300 rounded-full"></div>
              <span>Multi-location support</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <LoginForm defaultEmail={email} />
      </div>
    </div>
  );
}
